import React, { useState } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import DialogModal from '../../../../components/DialogModal';
import { StyledButton } from '../../../../components/Button';
import LoadingBadge from '../../../../components/LoadingBadge';
import CoachHistory, { HistoryEventType } from '../../../Model/CoachHistory';
import useToast from '../../../hooks/useToast';
import QuestionBlock from './components/QuestionBlock';

import {
  Container,
  Title,
  Section,
  SectionTitle,
} from './styles';
import texts from './texts.json';

const options = {
  YES: 'YES',
  NO: 'NO',
};

const ISACQuestionnaireModal = ({
  coachId,
  loggedInUserId,
  loggedInUserName,
  coachAnswers,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  const isCoachLeadView = !!(coachAnswers && !!Object.keys(coachAnswers).length);

  // Question definitions for mapping
  const yesNoOptions = [
    { label: texts.yes, value: options.YES },
    { label: texts.no, value: options.NO },
  ];
  const yesOnlyOptions = [
    { label: texts.yes, value: options.YES },
  ];

  const insideSalesQuestions = [
    {
      key: 'insideSalesRevenueShare',
      label: texts.insideSalesRevenueShare,
      options: yesOnlyOptions,
    },
    {
      key: 'insideSalesOptIn',
      label: texts.insideSalesOptIn,
      options: yesNoOptions,
    },
  ];

  const assistantCoachQuestions = [
    {
      key: 'assistantCoachRevenueShare',
      label: texts.assistantCoachRevenueShare,
      options: yesOnlyOptions,
    },
    {
      key: 'assistantCoachOptIn',
      label: texts.assistantCoachOptIn,
      options: yesNoOptions,
    },
  ];

  // State for answers
  const getInitialAnswers = () => {
    if (isCoachLeadView) {
      return {
        insideSalesRevenueShare: coachAnswers.insideSalesRevenueShare,
        insideSalesOptIn: coachAnswers.insideSalesOptIn,
        assistantCoachRevenueShare: coachAnswers.assistantCoachRevenueShare,
        assistantCoachOptIn: coachAnswers.assistantCoachOptIn,
      };
    }
    return {
      insideSalesRevenueShare: '',
      insideSalesOptIn: '',
      assistantCoachRevenueShare: '',
      assistantCoachOptIn: '',
    };
  };

  const [answers, setAnswers] = useState(getInitialAnswers());

  const handleAnswerChange = (key) => (value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  // Check if all fields are empty
  const areAllFieldsEmpty = () => Object.values(answers).every((value) => !value || value === '');

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.IS_AC_QUESTIONNAIRE_SUBMITTED,
        coachId,
        actionedBy: loggedInUserId,
        actionedByUserName: loggedInUserName,
        metadata: answers,
      });
      showToast(texts.saveSuccess);
      onClose();
    } catch (error) {
      showToast(format(texts.errorSavingQuestionnaire, { error: error.message }), { error: true });
    }
    setIsLoading(false);
  };

  return (
    <>
      <DialogModal
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <Container>
          <Title>{texts.title}</Title>

          {/* Inside Sales Section */}
          <Section>
            <SectionTitle>{texts.insideSalesSection}</SectionTitle>
            {insideSalesQuestions.map((q) => (
              <QuestionBlock
                key={q.key}
                label={q.label}
                options={q.options}
                selectedOption={answers[q.key]}
                onOptionChange={handleAnswerChange(q.key)}
                disabled={isCoachLeadView}
              />
            ))}
          </Section>

          {/* Assistant Coach Section */}
          <Section>
            <SectionTitle>{texts.assistantCoachSection}</SectionTitle>
            {assistantCoachQuestions.map((q) => (
              <QuestionBlock
                key={q.key}
                label={q.label}
                options={q.options}
                selectedOption={answers[q.key]}
                onOptionChange={handleAnswerChange(q.key)}
                disabled={isCoachLeadView}
              />
            ))}
          </Section>

          {!isCoachLeadView && (
            <StyledButton
              onClick={handleSave}
              variant="contained"
              disabled={isLoading || areAllFieldsEmpty()}
            >
              {isLoading ? <LoadingBadge /> : texts.save}
            </StyledButton>
          )}
        </Container>
      </DialogModal>
    </>
  );
};

ISACQuestionnaireModal.propTypes = {
  coachId: PropTypes.string.isRequired,
  loggedInUserId: PropTypes.string.isRequired,
  loggedInUserName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  coachAnswers: PropTypes.object,
};

ISACQuestionnaireModal.defaultProps = {
  coachAnswers: {},
};

export default ISACQuestionnaireModal;
